import React from 'react';
import { formatCOP, imageUrl } from '../services/api';
import { labelFormaPago, labelTipoDocumento } from './PedidoOpciones';
import { formatColombiaDateTime } from '../utils/colombiaTime';

const LOGO_URL =
  'https://ohbellezaoficial.com/cdn/shop/files/Logo_OH_BELLEZA-01_110x@2x.png?v=1724283344';

const PRINT_STYLES = `
  body { font-family: system-ui, sans-serif; margin: 24px; color: #18181b; }
  .factura-doc { max-width: 800px; margin: 0 auto; }
  .factura-head { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 20px; border-bottom: 2px solid #e91e96; padding-bottom: 16px; }
  .factura-brand { display: flex; align-items: center; gap: 12px; }
  .factura-logo { height: 52px; }
  .factura-meta-box { text-align: right; }
  .factura-meta-box h2 { margin: 0; font-size: 18px; }
  .factura-numero { font-weight: 800; color: #e91e96; margin: 4px 0; }
  .factura-parties { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
  .factura-party h3 { font-size: 11px; text-transform: uppercase; color: #71717a; margin-bottom: 6px; }
  .factura-party p { margin: 2px 0; font-size: 13px; }
  .factura-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .factura-table th { background: #fafafa; text-align: left; padding: 8px; border-bottom: 2px solid #e4e4e7; font-size: 11px; text-transform: uppercase; }
  .factura-table td { padding: 10px 8px; border-bottom: 1px solid #f4f4f5; vertical-align: top; }
  .col-qty, .col-price, .col-sub { text-align: right; white-space: nowrap; }
  .factura-item-img { width: 56px; height: 56px; object-fit: contain; border-radius: 8px; background: #fafafa; }
  .factura-item-img-empty { background: #f4f4f5; display: inline-block; }
  .factura-item-code { display: block; color: #e91e96; font-size: 12px; }
  .factura-item-name { display: block; font-weight: 600; }
  .factura-item-obs { display: block; font-size: 11px; color: #71717a; font-style: normal; }
  .factura-foot-total { font-size: 16px; color: #e91e96; }
  .factura-notas { font-size: 13px; margin-bottom: 12px; padding: 10px; background: #fafafa; border-radius: 8px; }
  .factura-actions { display: none !important; }
`;

function pickCliente(cliente = {}, pedido = {}) {
  const esSede = Boolean(pedido.es_sede || cliente.es_sede || pedido.padre_codigo || cliente.padre_codigo);
  const sedeEtiqueta = pedido.sede_etiqueta || cliente.sede_etiqueta || '';
  const padreCodigo = pedido.padre_codigo || cliente.padre_codigo || '';
  const nombreBase = pedido.cliente_nombre || cliente.nombre || '—';
  return {
    nombre: nombreBase,
    codigo: padreCodigo || pedido.sucursal_codigo || cliente.sucursal_codigo || '',
    codigoSede: esSede ? (pedido.sucursal_codigo || cliente.sucursal_codigo || '') : '',
    esSede,
    sedeEtiqueta,
    padreCodigo,
    documento: cliente.documento || '',
    telefono: pedido.cliente_telefono || cliente.telefono || '',
    ciudad: cliente.ciudad || '',
    zona: cliente.zona || '',
    direccion: pedido.sucursal_direccion || cliente.direccion || '',
    vendedor: pedido.vendedor || cliente.vendedor || '',
    notas: pedido.notas || cliente.notas || '',
  };
}

function pickMeta(pedidoMeta = {}, pedido = {}) {
  return {
    transportadora: pedido.transportadora || pedidoMeta.transportadora || '',
    transportadora_otra: pedidoMeta.transportadora_otra || '',
    horario_recibo: pedido.horario_recibo || pedidoMeta.horario_recibo || '',
    tipo_documento: pedido.tipo_documento ?? pedidoMeta.tipo_documento ?? '',
    forma_pago: pedido.forma_pago ?? pedidoMeta.forma_pago ?? '',
  };
}

function lineSubtotal(item) {
  return Number(item.subtotal ?? item.precio_unitario * item.cantidad) || 0;
}

/**
 * @param {object} props
 * @param {boolean} [props.hideMoney] — app vendedor: no mostrar precios ni total (cliente mira la tablet).
 */
export default function FacturaPreview({
  numero,
  fecha,
  cliente,
  pedidoMeta,
  pedido = {},
  items = [],
  subtotal,
  titulo = 'Pedido / Cotización',
  hideMoney = false,
  children,
}) {
  const c = pickCliente(cliente, pedido);
  const meta = pickMeta(pedidoMeta, pedido);
  const total = subtotal ?? items.reduce((s, it) => s + lineSubtotal(it), 0);
  const docLabel = labelTipoDocumento(meta.tipo_documento);
  const fechaStr = fecha
    ? formatColombiaDateTime(fecha)
    : formatColombiaDateTime(new Date());
  const transportadora =
    meta.transportadora === 'Otra' ? meta.transportadora_otra : meta.transportadora;
  const totalUnidades = items.reduce((s, it) => s + (Number(it.cantidad) || 0), 0);

  return (
    <article className={`factura-doc${hideMoney ? ' factura-doc--no-money' : ''}`}>
      <header className="factura-head">
        <div className="factura-brand">
          <img src={LOGO_URL} alt="¡Oh! Belleza — Mercadeo Cosmético" className="factura-logo" />
        </div>
        <div className="factura-meta-box">
          <h2>{titulo}</h2>
          {numero ? <p className="factura-numero">{numero}</p> : null}
          <p className="factura-fecha">{fechaStr}</p>
          {docLabel ? <span className="factura-doc-tipo">{docLabel}</span> : null}
        </div>
      </header>

      <div className="factura-parties">
        <section className="factura-party">
          <h3>Cliente / sucursal</h3>
          <p>
            <strong>{c.nombre}</strong>
          </p>
          {c.esSede ? (
            <p>
              Entrega en <strong>sucursal</strong>
              {c.padreCodigo ? ` del cliente ${c.padreCodigo}` : ''}
              {c.sedeEtiqueta ? `: ${c.sedeEtiqueta}` : ''}
            </p>
          ) : null}
          {c.codigo ? <p>Cód. {c.codigo}</p> : null}
          {c.codigoSede ? <p>Cód. sede: {c.codigoSede}</p> : null}
          {c.documento ? <p>Doc. {c.documento}</p> : null}
          {c.telefono ? <p>Tel. {c.telefono}</p> : null}
          {c.ciudad ? (
            <p>
              {c.ciudad}
              {c.zona ? ` · ${c.zona}` : ''}
            </p>
          ) : null}
          {c.direccion ? <p>{c.direccion}</p> : null}
        </section>
        <section className="factura-party">
          <h3>Condiciones</h3>
          {c.vendedor ? (
            <p>
              <span>Vendedor</span> <strong>{c.vendedor}</strong>
            </p>
          ) : null}
          {meta.forma_pago ? (
            <p>
              <span>Pago</span> <strong>{labelFormaPago(meta.forma_pago)}</strong>
            </p>
          ) : null}
          {transportadora ? (
            <p>
              <span>Transportadora</span> <strong>{transportadora}</strong>
            </p>
          ) : null}
          {meta.horario_recibo ? (
            <p>
              <span>Horario recibo</span> <strong>{meta.horario_recibo}</strong>
            </p>
          ) : null}
        </section>
      </div>

      {c.notas ? (
        <p className="factura-notas">
          <strong>Notas:</strong> {c.notas}
        </p>
      ) : null}

      <div className="factura-table-wrap">
        <table className="factura-table">
          <thead>
            <tr>
              <th className="col-foto">Foto</th>
              <th className="col-prod">Producto</th>
              <th className="col-qty">Cant.</th>
              {!hideMoney ? <th className="col-price">P. unit.</th> : null}
              {!hideMoney ? <th className="col-sub">Subtotal</th> : null}
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={item.lineId || item.id || idx}>
                <td className="col-foto">
                  {item.imagen ? (
                    <img src={imageUrl(item.imagen)} alt="" className="factura-item-img" />
                  ) : (
                    <div className="factura-item-img factura-item-img-empty" />
                  )}
                </td>
                <td className="col-prod">
                  <strong className="factura-item-code">{item.codigo}</strong>
                  <span className="factura-item-name">{item.nombre}</span>
                  {item.observaciones ? (
                    <em className="factura-item-obs">Obs: {item.observaciones}</em>
                  ) : null}
                </td>
                <td className="col-qty">
                  <strong>{item.cantidad}</strong>
                </td>
                {!hideMoney ? (
                  <td className="col-price">{formatCOP(item.precio_unitario)}</td>
                ) : null}
                {!hideMoney ? (
                  <td className="col-sub">
                    <strong>{formatCOP(lineSubtotal(item))}</strong>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              {hideMoney ? (
                <td colSpan={3} className="factura-foot-label">
                  {items.length} producto{items.length !== 1 ? 's' : ''} · {totalUnidades} unidades
                </td>
              ) : (
                <>
                  <td colSpan={2} className="factura-foot-label">
                    {items.length} producto{items.length !== 1 ? 's' : ''} · {totalUnidades} unidades
                  </td>
                  <td colSpan={2} className="factura-foot-label factura-foot-total-label">
                    Total
                  </td>
                  <td className="col-sub factura-foot-total">
                    <strong>{formatCOP(total)}</strong>
                  </td>
                </>
              )}
            </tr>
          </tfoot>
        </table>
      </div>

      {children ? <div className="factura-actions">{children}</div> : null}
    </article>
  );
}

function absolutizeImages(root) {
  root.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src');
    if (!src) return;
    if (src.startsWith('http') || src.startsWith('data:')) return;
    img.setAttribute('src', new URL(src, window.location.origin).href);
  });
}

function waitForImages(doc, cb) {
  const imgs = Array.from(doc.images || []);
  if (!imgs.length) {
    cb();
    return;
  }
  let pending = imgs.length;
  const done = () => {
    pending -= 1;
    if (pending <= 0) cb();
  };
  imgs.forEach((img) => {
    if (img.complete) done();
    else {
      img.onload = done;
      img.onerror = done;
    }
  });
}

export function printFactura(elementId) {
  const src = document.getElementById(elementId);
  if (!src) return;
  const win = window.open('', '_blank', 'noopener,noreferrer,width=900,height=700');
  if (!win) return;
  win.document.write(
    `<!DOCTYPE html><html><head><title>Pedido Oh Belleza</title><style>${PRINT_STYLES}</style></head><body></body></html>`
  );
  win.document.close();
  const clone = src.cloneNode(true);
  absolutizeImages(clone);
  win.document.body.appendChild(clone);
  waitForImages(win.document, () => {
    try {
      win.focus();
      win.print();
    } catch {
      /* ignore */
    }
  });
}
